'use client';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { MdCancel } from "react-icons/md";
import { IoChevronDown } from "react-icons/io5";
import EditDatasetLogic from './EditDatasetLogic';
import './EditDataset.css';
import { STATE_OPTIONS } from '../StateOptions';
import { FaEye, FaPlus } from "react-icons/fa";
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import { RiDeleteBin3Fill } from "react-icons/ri";

function EditDataset({ dataset, onSave, onCancel }) {
  const [acroFieldsList, setAcroFieldsList] = useState([]);
  const [jsonInput, setJsonInput] = useState("");         // back to local
  const acroFieldsTabRef = useRef("json");
  const getResolvedAcroList = () => {
    if (acroFieldsTabRef.current === "json") {
      try {
        const parsed = JSON.parse(jsonInput);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return Object.entries(parsed).map(([key, value]) => ({
            key,
            value: String(value),
          }));
        }
      } catch {
        // invalid JSON — fall back to list
      }
    }
    return acroFieldsList;
  };
  const { formData, errors, handleChange, handleSubmit, setUniversalFormFile, generatePresignedUrl } =
    EditDatasetLogic(dataset, onSave, onCancel, getResolvedAcroList);
  const handleTabChange = (tab) => {
    if (tab === "json") syncAcroListToJson();
    if (tab === "list") syncAcroJsonToList();
    acroFieldsTabRef.current = tab;
    setAcroFieldsTab(tab);
    setShowAcroFields(true);
  };

  const [stateSearch, setStateSearch] = useState("");
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("No file chosen");
  const [uploadedFileURL, setUploadedFileURL] = useState(null);
  const [showAcroFields, setShowAcroFields] = useState(false);
  const [acroFieldsTab, setAcroFieldsTab] = useState("json");
  const [jsonError, setJsonError] = useState("");
  const [showThresholds, setShowThresholds] = useState(false);


  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFileName(file.name);
      setUploadedFileURL(URL.createObjectURL(file));
      setUniversalFormFile(file);
    } else {
      setUploadedFileName("No file chosen");
      setUploadedFileURL(null);
      setUniversalFormFile(null);
    }
  };

  const handleJsonInput = (value) => {
    setJsonInput(value);

    if (!value.trim()) {
      setJsonError("");
      setAcroFieldsList([]);
      return;
    }

    try {
      const parsed = JSON.parse(value);

      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setJsonError("Invalid JSON. Please check your syntax.");
        return;
      }

      setJsonError("");
      const converted = Object.entries(parsed).map(([key, val]) => ({
        key,
        value: String(val),
      }));
      setAcroFieldsList(converted);
    } catch {
      setJsonError("Invalid JSON. Please check your syntax.");
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (
      formData.entryType === "Request Based PDF Entry" &&
      acroFieldsTab === "json" &&
      jsonInput.trim() &&
      jsonError
    ) {
      return;
    }

    handleSubmit(e);
  };
  const syncAcroListToJson = () => {
    const obj = {};

    acroFieldsList.forEach((item) => {
      if (item.key) {
        obj[item.key] = item.value;
      }
    });

    setJsonInput(JSON.stringify(obj, null, 2));
  };

  const syncAcroJsonToList = () => {
    try {
      const parsed = JSON.parse(jsonInput);

      const converted = Object.entries(parsed).map(([key, value]) => ({
        key,
        value: String(value),
      }));

      setAcroFieldsList(converted);
    } catch {
      setAcroFieldsList([]);
    }
  };

  const handleDeleteAcroField = (index) => {
    setAcroFieldsList((prev) =>
      prev.filter((_, idx) => idx !== index)
    );
  };

  useEffect(() => {
    const selectedState = STATE_OPTIONS.find(
      (stateOption) => stateOption.value === formData.state
    );

    setStateSearch(selectedState ? selectedState.label : "");
  }, [formData.state]);

  useEffect(() => {

    if (formData?.acroFields) {

      // JSON VIEW
      setJsonInput(
        JSON.stringify(formData.acroFields, null, 2)
      );

      // LIST VIEW
      const converted = Object.entries(formData.acroFields).map(
        ([key, value]) => ({
          key,
          value: String(value),
        })
      );

      setAcroFieldsList(converted);
    }

  }, [formData?.acroFields]);

  const filteredStateOptions = useMemo(() => {
    const normalizedSearch = stateSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return STATE_OPTIONS;
    }

    return STATE_OPTIONS.filter((stateOption) => {
      const normalizedLabel = stateOption.label.toLowerCase();
      const normalizedValue = stateOption.value.toLowerCase();

      return (
        normalizedLabel.includes(normalizedSearch) ||
        normalizedValue.includes(normalizedSearch)
      );
    });
  }, [stateSearch]);

  const updateSelectedState = (value) => {
    handleChange({
      target: {
        name: "state",
        value,
      },
    });
  };

  const handleStateInputChange = (e) => {
    const inputValue = e.target.value;

    setStateSearch(inputValue);
    setIsStateDropdownOpen(true);
    updateSelectedState("");
  };

  const handleStateSelect = (stateOption) => {
    setStateSearch(stateOption.label);
    updateSelectedState(stateOption.value);
    setIsStateDropdownOpen(false);
  };

  const handleStateBlur = () => {
    window.setTimeout(() => {
      setIsStateDropdownOpen(false);

      const normalizedSearch = stateSearch.trim().toLowerCase();
      const exactMatch = STATE_OPTIONS.find((stateOption) => {
        const normalizedLabel = stateOption.label.toLowerCase();
        const normalizedValue = stateOption.value.toLowerCase();

        return (
          normalizedLabel === normalizedSearch ||
          normalizedValue === normalizedSearch
        );
      });

      if (exactMatch) {
        setStateSearch(exactMatch.label);
        updateSelectedState(exactMatch.value);
        return;
      }

      if (!normalizedSearch) {
        updateSelectedState("");
        return;
      }

      if (!formData.state) {
        setStateSearch("");
      }
    }, 150);
  };

  const handleStateDropdownToggle = (e) => {
    e.preventDefault();
    setIsStateDropdownOpen((prev) => !prev);
  };

  return (
    <div className="editUserModal">
      <div className="editUserBody">
        <div className="editUserHeader">
          <span>Edit Dataset</span>
          <MdCancel size={24} className="closeEditForm" onClick={onCancel} />
        </div>

        <div className="editUserBodyMain">
          <form onSubmit={handleFormSubmit}>
            <div className="editUserFields">

              {/* BASIC FIELDS */}
              {[
                { id: "datasetID", label: "Dataset ID", required: true, placeholder: "Enter Dataset ID", readonly: true },
                { id: "datasetName", label: "Dataset Name", required: true, placeholder: "Enter Dataset Name" },
                { id: "datasetDisplayName", label: "Display Name", required: true, placeholder: "Enter Display Name" },
              ].map((field) => (
                <div key={field.id} className="form-group inputsSet">
                  <label htmlFor={field.id}>
                    {field.label}
                    {field.required && <span style={{ color: "#dc3545" }}> *</span>}
                  </label>

                  <input
                    type="text"
                    id={field.id}
                    name={field.id}
                    value={formData[field.id]}
                    onChange={field.readonly ? undefined : handleChange}
                    readOnly={field.readonly || false}
                    placeholder={field.placeholder}
                    className={`form-control addInputUser ${errors[field.id] ? "is-invalid" : ""} ${field.readonly ? "readonly-field" : ""}`}
                  />

                  {errors[field.id] && (
                    <div className="error-text-Dataset">{errors[field.id]}</div>
                  )}
                </div>
              ))}

              <div className="form-group inputsSet stateFieldWrap">
                <label htmlFor="stateSearch">
                  State
                  <span style={{ color: "#dc3545" }}> *</span>
                </label>

                <div className="stateInputWrap">
                  <input
                    type="text"
                    id="stateSearch"
                    value={stateSearch}
                    onChange={handleStateInputChange}
                    onFocus={() => setIsStateDropdownOpen(true)}
                    onBlur={handleStateBlur}
                    placeholder="Enter State Name or Abbreviation"
                    autoComplete="off"
                    className={`form-control addInputUser stateInputControl ${errors.state ? "is-invalid" : ""}`}
                  />
                  <button
                    type="button"
                    className="stateDropdownToggle"
                    onMouseDown={handleStateDropdownToggle}
                    aria-label="Toggle state dropdown"
                  >
                    <IoChevronDown
                      size={16}
                    // className={isStateDropdownOpen ? "stateChevron open" : "stateChevron"}
                    />
                  </button>
                </div>

                {isStateDropdownOpen && (
                  <div className="stateDropdownMenu">
                    {filteredStateOptions.length > 0 ? (
                      filteredStateOptions.map((stateOption) => (
                        <button
                          key={stateOption.value}
                          type="button"
                          className={`stateDropdownOption ${formData.state === stateOption.value ? "active" : ""}`}
                          onMouseDown={() => handleStateSelect(stateOption)}
                        >
                          <span>{stateOption.label}</span>
                          <span>{stateOption.value}</span>
                        </button>
                      ))
                    ) : (
                      <div className="stateDropdownEmpty">No matching states</div>
                    )}
                  </div>
                )}

                {errors.state && (
                  <div className="error-text-Dataset">{errors.state}</div>
                )}
              </div>


              {/* ENTRY TYPE */}
              <div className="form-group inputsSet" style={{ marginTop: "5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input type="radio" name="entryType"
                    value="Request Based PDF Entry"
                    checked={formData.entryType === "Request Based PDF Entry"}
                    onChange={handleChange}
                  />
                  <span>Request Based PDF Entry</span>
                </div>
              </div>

              <div className="form-group inputsSet" style={{ marginTop: "5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input type="radio" name="entryType"
                    value="Counter Based WebSite Entry"
                    checked={formData.entryType === "Counter Based WebSite Entry"}
                    onChange={handleChange}
                  />
                  <span>Counter Based WebSite Entry</span>
                </div>
                {formData.entryType === "Counter Based WebSite Entry" && (
                  <div className="entry-flex-row-edit">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="radio" name="objectType" value="Grouped Object"
                        checked={formData.objectType === "Grouped Object"}
                        onChange={handleChange}
                      />
                      <span>Grouped Object</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="radio" name="objectType" value="Individual Object"
                        checked={formData.objectType === "Individual Object"}
                        onChange={handleChange}
                      />
                      <span>Individual Object</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="form-group inputsSet" style={{ marginTop: "5px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <input type="radio" name="entryType"
                    value="Date Range Based PDF Entry"
                    checked={formData.entryType === "Date Range Based PDF Entry"}
                    onChange={handleChange}
                  />
                  <span>Date Range Based PDF Entry</span>
                </div>
                {formData.entryType === "Date Range Based PDF Entry" && (
                  <div className="entry-flex-row-edit">
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="radio" name="objectType" value="Grouped Object"
                        checked={formData.objectType === "Grouped Object"}
                        onChange={handleChange}
                      />
                      <span>Grouped Object</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="radio" name="objectType" value="Individual Object"
                        checked={formData.objectType === "Individual Object"}
                        onChange={handleChange}
                      />
                      <span>Individual Object</span>
                    </div>
                  </div>
                )}
              </div>

              {/* REQUEST */}
              {formData.entryType === "Request Based PDF Entry" && (
                <>
                  <div className="range-row">
                    {/* <div className="form-group inputsSet">
                      <label>Zoho Department ID</label>
                      <input
                        type="text"
                        name="zohoDepartmentID"
                        value={formData.zohoDepartmentID}
                        onChange={handleChange}
                        className="form-control addInputUser"
                        placeholder="Enter Zoho Department ID "
                      />
                    </div> */}
                  </div>
                  <div className="global-requestor-header">
                    <h4>Global Requestor Information</h4>
                  </div>
                  {/* Universal Form Upload */}
                  <div
                    className="smtp-settings-container column-field"
                    style={{ marginTop: "8px" }}
                  >
                    <h5>Form Upload</h5>

                    {/* UPLOAD + BUTTONS ROW */}
                    <div className="universal-upload-row">

                      {/* EXISTING / NEW FILE */}
                      <div className="universal-upload-container">

                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handleFileChange}
                          className="form-control addInputUser"
                        />

                        {formData.universalFormS3Key && !uploadedFileURL && (
                          <div className="existing-file-row">
                            <span className="universal-upload-filename">
                              {formData.universalFormS3Key.split("/").pop()}
                            </span>
                            <FaEye
                              size={18}
                              className="upload-preview-icon"
                              onClick={() => generatePresignedUrl(formData.universalFormS3Key)}
                            />
                          </div>
                        )}

                        {uploadedFileURL && (
                          <div className="existing-file-row">
                            <span className="universal-upload-filename">
                              {uploadedFileName}
                            </span>
                            <FaEye
                              size={18}
                              className="upload-preview-icon"
                              onClick={() => window.open(uploadedFileURL, "_blank")}
                            />
                          </div>
                        )}

                      </div>
                      {/* JSON / LIST BUTTONS */}
                      <div className="dsjson-list-tabs1 upload-tabs">

                        <button
                          type="button"
                          className={`dstab-btn1 ${acroFieldsTab === "json" ? "active" : ""}`}
                          onClick={() => handleTabChange("json")}
                        >
                          JSON
                        </button>

                        <button
                          type="button"
                          className={`dstab-btn1 ${acroFieldsTab === "list" ? "active" : ""}`}
                          onClick={() => handleTabChange("list")}
                        >
                          LIST
                        </button>

                      </div>

                    </div>

                    {/* ACCORDION */}
                    <div className="accordion-item1 mt-3">

                      <div
                        className="accordion-header"
                        onClick={() => setShowAcroFields(!showAcroFields)}
                      >
                        <span>
                          {acroFieldsTab === "json"
                            ? "AcroFields JSON"
                            : "AcroFields List"}
                        </span>

                        <span>
                          {showAcroFields
                            ? <IoIosArrowUp />
                            : <IoIosArrowDown />}
                        </span>
                      </div>

                      {showAcroFields && (

                        <div className="accordion-content">

                          {/* JSON VIEW */}
                          {acroFieldsTab === "json" && (
                            <div className="form-group input-section">
                              <textarea
                                className="form-control json-textarea"
                                placeholder='Enter JSON Object'
                                value={jsonInput}
                                onChange={(e) => handleJsonInput(e.target.value)}
                                rows={6}
                              />
                              {jsonError && (
                                <div className="error-json-Dataset">
                                  {jsonError}
                                </div>
                              )}
                            </div>
                          )}

                          {/* LIST VIEW */}
                          {acroFieldsTab === "list" && (
                            <div>

                              <div className="dslist-header-row1">
                                <span
                                  className="add-item-btn1"
                                  onClick={() => {
                                    setAcroFieldsList((prev) => [
                                      { key: "", value: "" },
                                      ...prev,
                                    ]);
                                  }}
                                >
                                  <FaPlus size={16} />
                                  Add Field
                                </span>
                              </div>

                              <table className="dslist-table1">
                                <thead>
                                  <tr>
                                    <th>Field Name</th>
                                    <th>Field Value</th>
                                    <th>Action</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {acroFieldsList.map((row, index) => (
                                    <tr key={index}>
                                      <td>
                                        <input
                                          className="form-control list-input1"
                                          value={row.key}
                                          onChange={(e) => {
                                            const updated = [...acroFieldsList];
                                            updated[index].key = e.target.value;
                                            setAcroFieldsList(updated);
                                          }}
                                        />
                                      </td>

                                      <td>
                                        <input
                                          className="form-control list-input1"
                                          value={row.value}
                                          onChange={(e) => {
                                            const updated = [...acroFieldsList];
                                            updated[index].value = e.target.value;

                                            setAcroFieldsList(updated);

                                            const obj = {};

                                            updated.forEach((item) => {
                                              if (item.key) {
                                                obj[item.key] = item.value;
                                              }
                                            });

                                            setJsonInput(JSON.stringify(obj, null, 2));
                                          }}
                                        />
                                      </td>

                                      <td>
                                        <RiDeleteBin3Fill
                                          style={{
                                            color: "#dc3545",
                                            cursor: "pointer",
                                          }}
                                          size="16px"
                                          onClick={() => handleDeleteAcroField(index)}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>

                              </table>

                            </div>
                          )}

                        </div>
                      )}

                    </div>
                  </div>
                  <div className="smtp-settings-container column-field" style={{ marginTop: "8px" }}>
                    <h5>SMTP Settings</h5>

                    {/* From Email (REQUIRED) */}
                    <div className="form-group smtp-from-ED">
                      <label>From Email <span style={{ color: "#dc3545" }}>*</span></label>
                      <input
                        type="text"
                        name="smtpFromEmail"
                        value={formData.smtpFromEmail || ''}
                        onChange={handleChange}
                        className={`form-control addInputUser ${errors.smtpFromEmail ? "is-invalid" : ""}`}
                        placeholder="Enter From Email"
                      />
                      {errors.smtpFromEmail && <div className="error-text-Dataset">{errors.smtpFromEmail}</div>}
                    </div>

                    <div className="form-group smtp-from-ED">
                      <label>Reply To Email <span style={{ color: "#dc3545" }}>*</span></label>
                      <input
                        type="text"
                        name="replyToEmail"
                        value={formData.replyToEmail || ''}
                        onChange={handleChange}
                        className={`form-control addInputUser ${errors.replyToEmail ? "is-invalid" : ""}`}
                        placeholder="Enter Reply To Email"
                      />
                      {errors.replyToEmail && (
                        <div className="error-text-Dataset">{errors.replyToEmail}</div>
                      )}
                    </div>

                    {/* Subject (REQUIRED) */}
                    <div className="form-group smtp-from-ED">
                      <label>Subject </label>
                      <input
                        type="text"
                        name="smtpSubject"
                        value={formData.smtpSubject || ''}
                        onChange={handleChange}
                        // className={`form-control addInputUser ${errors.smtpSubject ? "is-invalid" : ""}`}
                        className="form-control addInputUser"
                        placeholder="Enter Subject"
                      />
                      {/* {errors.smtpSubject && <div className="error-text-Dataset">{errors.smtpSubject}</div>} */}
                    </div>

                  </div>
                  {/* THRESHOLDS */}
                  <div className="ds-accordion-item mt-3">
                    <div
                      className="ds-accordion-header"
                      onClick={() => setShowThresholds(!showThresholds)}
                    >
                      <span>Thresholds</span>
                      <span>{showThresholds ? <IoIosArrowUp /> : <IoIosArrowDown />}</span>
                    </div>

                    {showThresholds && (
                      <div className="ds-accordion-content">
                        <div className="ds-thresholds-fields">

                          <div className="ds-threshold-group">
                            <label>Daily Entry Limit</label>
                            <input
                              type="number"
                              name="dailyEntryLimit"
                              value={formData.dailyEntryLimit ?? ""}
                              min={0}
                              onChange={handleChange}
                              className="form-control addInputUser"
                              placeholder="Enter Daily Entry Limit"
                            />
                          </div>

                          <div className="ds-threshold-group">
                            <label>Days Allowed for QA Review</label>
                            <input
                              type="number"
                              name="qaCompletionDays"
                              value={formData.qaCompletionDays ?? ""}
                              min={0}
                              max={9}
                              onChange={handleChange}
                              className="form-control addInputUser"
                              placeholder="Enter Days Allowed for QA Review"
                            />
                          </div>

                          <div className="ds-threshold-group">
                            <label>Days Allowed to Approve Ignored Files/PDFs</label>
                            <input
                              type="number"
                              name="ignoredFileApprovalDays"
                              value={formData.ignoredFileApprovalDays ?? ""}
                              min={0}
                              max={9}
                              onChange={handleChange}
                              className="form-control addInputUser"
                              placeholder="Enter Days Allowed to Approve Ignored Files/PDFs"
                            />
                          </div>

                        </div>
                      </div>
                    )}
                  </div>
                  {/* OPRA Request Send Enabled */}
                  <div className="opra-checkbox-row">
                    <input
                      type="checkbox"
                      id="datasetOPRARequestSendEnable"
                      name="datasetOPRARequestSendEnable"
                      checked={formData.datasetOPRARequestSendEnable ?? false}
                      onChange={(e) =>
                        handleChange({ target: { name: "datasetOPRARequestSendEnable", checked: e.target.checked, value: e.target.checked } })
                      }
                    />
                    <label htmlFor="datasetOPRARequestSendEnable">
                      OPRA Request Send Enabled
                    </label>
                  </div>
                </>
              )}

            </div>

            <div className="button-right-edit">
              <button type="submit" className="edit-button-user">
                Update Dataset
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default EditDataset;
